import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
  type ReactNode,
} from 'react'
import { createPortal } from 'react-dom'
import { useNavigate } from 'react-router-dom'
import { Button } from '@diwata/ironvale'
import { useSelection } from '@/store/selection'
import type { GraphNode } from '@/api/graph'
import './entity-peek.css'

// EntityPeek — the one thing a [[citation]] does, everywhere it appears.
//
// A cited entity used to `select()` the node on click, which sets the selection store but
// does NOT navigate. From the Tala rail or a mission artifact that read as a dead click —
// nothing moved. The peek closes that: a click opens a small popover anchored to the
// citation (name, type, domain, one stat) with an explicit "Open in Constellation →" that
// BOTH selects and routes to /graph, so the dossier is open on arrival.
//
// It lives as a provider + hook so the citation primitive stays a pure render function:
// `dw-cite` buttons hand us their node and their own DOM element, and the single portalled
// popover does the rest. The ego ("You", self:true) is a normal GraphNode — no special case.
//
// The panel portals to document.body and is positioned from the anchor's live rect (clamped
// to the viewport), the same approach as FlowDiagram / ironvale's Tooltip — so it can never be
// clipped by an overflow ancestor or stack under the rails.

type PeekTarget = { node: GraphNode; anchor: HTMLElement }

interface PeekContext {
  openPeek: (node: GraphNode, anchor: HTMLElement) => void
}

const Ctx = createContext<PeekContext | null>(null)

/**
 * Open the entity peek for a cited node, anchored to its clicked element. Safe to call outside
 * a provider — it degrades to a no-op rather than throwing (a citation with nowhere to peek
 * simply does nothing, never crashes the surface it's on).
 */
export function useEntityPeek(): PeekContext {
  return useContext(Ctx) ?? { openPeek: () => {} }
}

/** The one cheap stat a GraphNode already carries — no fetch. Score wins, else visible heat,
 *  else confidence, else the count of enrichment attributes. Null when the node is bare. */
function statOf(node: GraphNode): { label: string; value: string } | null {
  if (node.score != null) return { label: 'score', value: String(node.score) }
  if (typeof node.heat === 'number' && node.heat > 0) return { label: 'heat', value: String(node.heat) }
  if (typeof node.confidence === 'number') return { label: 'confidence', value: `${Math.round(node.confidence * 100)}%` }
  const attrCount = node.attributes ? Object.keys(node.attributes).length : 0
  if (attrCount > 0) return { label: attrCount === 1 ? 'attribute' : 'attributes', value: String(attrCount) }
  return null
}

export function EntityPeekProvider({ children }: { children: ReactNode }) {
  const navigate = useNavigate()
  const select = useSelection((s) => s.select)
  const [target, setTarget] = useState<PeekTarget | null>(null)
  const [pos, setPos] = useState<{ top: number; left: number } | null>(null)
  const panelRef = useRef<HTMLDivElement>(null)

  const openPeek = useCallback((node: GraphNode, anchor: HTMLElement) => {
    setPos(null)
    setTarget({ node, anchor })
  }, [])

  const close = useCallback(() => setTarget(null), [])

  // Dismiss on Escape and on any click outside — "outside" means outside BOTH the panel and
  // the anchor (clicking the anchor again reopens through the citation's own handler).
  useEffect(() => {
    if (!target) return
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.stopPropagation()
        close()
      }
    }
    const onDown = (e: MouseEvent) => {
      const t = e.target as Node
      if (panelRef.current?.contains(t)) return
      if (target.anchor.contains(t)) return
      close()
    }
    document.addEventListener('keydown', onKey, true)
    document.addEventListener('mousedown', onDown)
    return () => {
      document.removeEventListener('keydown', onKey, true)
      document.removeEventListener('mousedown', onDown)
    }
  }, [target, close])

  // Move focus into the panel when it opens (focus-trap-lite: the primary action is the one
  // affordance keyboard users want), and place the panel against the anchor's live rect —
  // below it, flipped above when there's more room, then clamped inside the viewport margins.
  useLayoutEffect(() => {
    if (!target) {
      setPos(null)
      return
    }
    const place = () => {
      const panel = panelRef.current
      if (!panel) return
      const r = target.anchor.getBoundingClientRect()
      const pw = panel.offsetWidth
      const ph = panel.offsetHeight
      const vw = window.innerWidth
      const vh = window.innerHeight
      const gap = 8
      const margin = 12

      let top = r.bottom + gap
      const roomBelow = vh - r.bottom - gap - margin
      const roomAbove = r.top - gap - margin
      if (roomBelow < ph && roomAbove > roomBelow) top = r.top - gap - ph
      top = Math.max(margin, Math.min(top, vh - ph - margin))

      // Left-align to the anchor (a citation sits mid-sentence), then clamp on-screen.
      let left = r.left
      left = Math.max(margin, Math.min(left, vw - pw - margin))

      setPos({ top, left })
    }
    place()
    // Move focus to the primary action (focus-trap-lite) — the one affordance keyboard users want.
    panelRef.current?.querySelector<HTMLButtonElement>('button')?.focus()
    window.addEventListener('resize', place)
    window.addEventListener('scroll', place, true)
    return () => {
      window.removeEventListener('resize', place)
      window.removeEventListener('scroll', place, true)
    }
  }, [target])

  function openInConstellation() {
    if (!target) return
    const n = target.node
    // Select FIRST so the dossier has its star, THEN route — the constellation opens on it.
    select({
      id: n.id,
      name: n.name,
      type: n.type,
      world: n.world,
      score: n.score,
      domain: n.domain,
      attributes: n.attributes ?? null,
    })
    close()
    navigate('/graph')
  }

  const stat = target ? statOf(target.node) : null

  return (
    <Ctx.Provider value={{ openPeek }}>
      {children}
      {target &&
        typeof document !== 'undefined' &&
        createPortal(
          <div
            ref={panelRef}
            role="dialog"
            aria-label={`${target.node.name} — quick look`}
            className="dw-peek"
            style={{
              top: pos ? `${pos.top}px` : 0,
              left: pos ? `${pos.left}px` : 0,
              visibility: pos ? 'visible' : 'hidden',
            }}
            onKeyDown={(e) => {
              // Focus-trap-lite: keep Tab within the panel's own controls.
              if (e.key === 'Tab') {
                const focusables = panelRef.current?.querySelectorAll<HTMLElement>(
                  'button, [href], [tabindex]:not([tabindex="-1"])',
                )
                if (!focusables || focusables.length === 0) return
                const first = focusables[0]
                const last = focusables[focusables.length - 1]
                if (e.shiftKey && document.activeElement === first) {
                  e.preventDefault()
                  last.focus()
                } else if (!e.shiftKey && document.activeElement === last) {
                  e.preventDefault()
                  first.focus()
                }
              }
            }}
          >
            <div className="dw-peek__name">{target.node.name}</div>
            <div className="dw-peek__meta">
              <span className="dw-peek__type" title="What kind of star this is — its entity type in the domain's vocabulary.">
                {target.node.type}
              </span>
              {target.node.domain && <span className="dw-peek__chip">{target.node.domain}</span>}
              {stat && (
                <span className="dw-peek__chip dw-peek__chip--stat">
                  {stat.label} {stat.value}
                </span>
              )}
            </div>
            <div className="dw-peek__actions">
              <Button size="sm" variant="primary" onClick={openInConstellation}>
                Open in Constellation →
              </Button>
            </div>
          </div>,
          document.body,
        )}
    </Ctx.Provider>
  )
}
